import { useCallbackRef } from "../lifecycle";
import {
  BillingAddress,
  updateCheckoutState,
} from "src/utils/redux/slices/checkout";
import {
  createOrderRequest,
  previewBillingCycles,
  getOrderRequest,
} from "src/utils/api/purchase/ordersCheckoutApi";
import { useAppDispatch, useCheckout, usePromoCode } from "../redux";
import {
  setCheckoutOrders,
  setHasInitializationFailed,
  setCheckoutSaleItem,
} from "src/utils/redux/slices/checkoutOrders";
import { errorConstants } from "src/utils/services/PurchaseErrorMessages";
import { convertCyclesToPlan } from "src/utils/orderConversions";
import {
  legacyPurchaseRoot,
  setRecommendedPlan,
  setTrialFee,
} from "src/utils/redux/slices/recommendedPlan";
import { captureException } from "src/utils/error";
import { trackTask } from "src/utils/monitoring/events/tasks";
import {
  requiresPhysicalAddress,
  hasValidPhysicalAddress,
} from "src/utils/addressValidation";
import { selectResolvedPhysicalAddressShortRegion } from "src/utils/redux/slices/checkoutPhysicalAddress/address-selectors";
import { useSelector } from "react-redux";
import {
  applyPromoCode,
  hasValidBillingAddress,
} from "src/utils/api/purchase/checkout";
import {
  convertMoneyProtoToNumber,
  convertNumberToMoney,
} from "src/utils/noomContracts/moneyProto";
import { MoneyProto } from "@noom/noom-contracts/noom_contracts/lib/money";
import {
  setPromoError,
  updatePromoDiscountAmount,
  updatePromoOrderStatus,
} from "src/utils/redux/slices/promoCode";
import { trackBuyflowEvent } from "src/utils/api/tracker";
import i18n from "src/locales";
import { BillingCyclePreviewProto } from "@noom/noom-contracts/noom_contracts/billing/bms/subscriptions/billing_cycle_preview";
import { PreviewBillingCyclesResponseProto } from "@noom/noom-contracts/noom_contracts/billing/bms/subscriptions/bms_subscription_service";
import { CustomerDetailsProto } from "@noom/noom-contracts/noom_contracts/billing/bms/common/shared";
import { PurchaseOperationProto } from "@noom/noom-contracts/noom_contracts/billing/bms/orders/order_operation";
import getStore from "src/utils/redux/store";
import { isApplePurchaseLinkoutEnabled } from "src/utils/userSegment/features";
import { getActiveUser } from "@noom/hive-auth";

export const DEFAULT_ORDERS_CURRENCY = "USD";

function remapURLNoomPlanId(noomPlanId: string) {
  if (noomPlanId && /\/purchase(-hm)?\//.test(window.location.pathname)) {
    const { history } = window;
    history.replaceState(
      history.state,
      "",
      legacyPurchaseRoot() + window.location.hash
    );
  }
}

export function useOrdersCheckoutMethods() {
  const dispatch = useAppDispatch();
  const accessCode = getActiveUser()?.accessCode;
  const { billingAddress: currentBillingAddress } = useCheckout();
  const resolvedPhysicalAddress = useSelector(
    selectResolvedPhysicalAddressShortRegion
  );
  const { promoCodeApplied, promoDiscountPercentage, promoDiscountAmount } =
    usePromoCode();

  function getPaymentSchedule(order) {
    return order.executionActions[0].createSubscription.paymentSchedule;
  }

  function formatAddress(address?: BillingAddress) {
    if (!address) {
      return undefined;
    }
    return {
      addressLine1: address.address1,
      addressLine2: address.address2,
      city: address.city,
      zoneCode: address.region,
      postalCode: address.zipcode,
      countryCode: address.country,
    };
  }

  function buildCustomerDetails({
    billingAddress,
    shippingAddress,
  }: {
    billingAddress?: BillingAddress;
    shippingAddress?: BillingAddress;
  }): CustomerDetailsProto | undefined {
    const billingAddressProto = formatAddress(billingAddress);
    const shippingAddressProto = formatAddress(shippingAddress);

    if (!billingAddressProto && !shippingAddressProto) {
      return undefined;
    }

    return {
      ...(billingAddressProto && { billingAddress: billingAddressProto }),
      ...(shippingAddressProto && { shippingAddress: shippingAddressProto }),
    };
  }

  function buildPurchaseOperation({
    saleItemId,
    trialSaleItemId,
    percentageDiscount,
    flatDiscount,
  }: {
    saleItemId: string;
    trialSaleItemId?: string;
    percentageDiscount?: number;
    flatDiscount?: MoneyProto;
  }): PurchaseOperationProto {
    const purchaseOperation: PurchaseOperationProto = {
      saleItemId,
      metadata: {
        purchaseFlow: "WEB",
      },
    };

    if (trialSaleItemId) {
      purchaseOperation.trialSaleItemId = trialSaleItemId;
    }

    if (flatDiscount) {
      purchaseOperation.discountSummary = {
        fixedDiscount: {
          amount: flatDiscount,
          percentageDiscountConvertedToFixed: false,
        },
      };
    }

    if (percentageDiscount) {
      purchaseOperation.discountSummary = {
        percentageDiscount: {
          percentage: {
            value: percentageDiscount.toString(),
          },
        },
      };
    }

    return purchaseOperation;
  }

  function buildCreateOrderPayload({
    billingAddress,
    shippingAddress,
    percentageDiscount,
    flatDiscount,
    saleItemId,
    trialSaleItemId,
  }: {
    billingAddress?: BillingAddress;
    shippingAddress?: BillingAddress;
    percentageDiscount?: number;
    flatDiscount?: MoneyProto;
    saleItemId: string;
    trialSaleItemId?: string;
  }) {
    const purchaseOperation = buildPurchaseOperation({
      saleItemId,
      trialSaleItemId,
      percentageDiscount,
      flatDiscount,
    });
    const customerDetails = buildCustomerDetails({
      billingAddress,
      shippingAddress,
    });

    return {
      customerDetails,
      purchaseOperation,
    };
  }

  function extractBillingCycles({
    billingCycles,
  }: PreviewBillingCyclesResponseProto): {
    initialBillingCycle?: BillingCyclePreviewProto;
    recurringBillingCycle?: BillingCyclePreviewProto;
  } {
    if (!billingCycles?.length) {
      throw new Error("Invalid billing cycles preview");
    }
    if (billingCycles.length === 1) {
      return {
        recurringBillingCycle: billingCycles[0],
      };
    }
    return {
      initialBillingCycle: billingCycles[0],
      recurringBillingCycle: billingCycles[1],
    };
  }

  async function createOrderAndBillingCyclePreview(
    usePreviewMode = true,
    billingAddress?: BillingAddress,
    shippingAddress?: BillingAddress,
    percentageDiscount?: number,
    flatDiscount?: MoneyProto
  ): Promise<{
    initialBillingCycle?: BillingCyclePreviewProto;
    recurringBillingCycle?: BillingCyclePreviewProto;
  }> {
    const state = getStore().getState();
    // Get the sale item directly from state to avoid using stale state from the hook
    const { saleItem: newSaleItem } = state.checkoutOrders;
    const { saleItemId, trialSaleItemId, pspPlanId, legacyNoomPlanId } =
      newSaleItem;

    const { customerDetails, purchaseOperation } = buildCreateOrderPayload({
      billingAddress,
      shippingAddress,
      percentageDiscount,
      flatDiscount,
      saleItemId,
      trialSaleItemId,
    });

    const order = await createOrderRequest(
      usePreviewMode ? undefined : accessCode,
      usePreviewMode,
      customerDetails,
      purchaseOperation
    );
    const billingCyclesPreview = await previewBillingCycles({
      paymentSchedule: getPaymentSchedule(order),
    });
    const { initialBillingCycle, recurringBillingCycle } =
      extractBillingCycles(billingCyclesPreview);
    dispatch(
      setCheckoutOrders({
        order,
        initialBillingCycle,
        recurringBillingCycle,
      })
    );
    // All the med checkout UI was migrated to use the new checkout orders slice
    // but we still need to convert the plan to the recommended plan for the pixel tracking,
    // purchase logic and purchase tracking that have not been migrated yet.
    // TODO: MGEXP-3025
    const recommendedPlan = convertCyclesToPlan({
      initialCycle: initialBillingCycle,
      recurringCycle: recurringBillingCycle,
      pspPlanId,
      legacyNoomPlanId,
      trialSaleItemId,
    });
    dispatch(setRecommendedPlan(recommendedPlan));
    remapURLNoomPlanId(legacyNoomPlanId);

    // For Apple Purchase Linkout, trial fee is always 0
    if (isApplePurchaseLinkoutEnabled()) {
      dispatch(setTrialFee(0));
    }

    return { initialBillingCycle, recurringBillingCycle };
  }

  function getValidAddressForTaxCalculation() {
    if (requiresPhysicalAddress()) {
      if (hasValidPhysicalAddress()) {
        return { shippingAddress: resolvedPhysicalAddress };
      }
      return {};
    }
    if (hasValidBillingAddress()) {
      return { billingAddress: currentBillingAddress };
    }
    return {};
  }

  function getDiscountsFromSlice() {
    if (!promoCodeApplied) return {};
    if (promoDiscountPercentage && promoDiscountPercentage !== 0) {
      return { percentageDiscount: promoDiscountPercentage };
    }
    if (promoDiscountAmount && promoDiscountAmount !== 0) {
      return {
        flatDiscount: convertNumberToMoney(
          promoDiscountAmount,
          DEFAULT_ORDERS_CURRENCY
        ),
      };
    }
    return {};
  }

  async function previewOrderWithContext({
    applyAddress = false,
    reapplyExistingPromo = false,
    percentageDiscountOverride,
    flatDiscountOverride,
  }: {
    applyAddress?: boolean;
    reapplyExistingPromo?: boolean;
    percentageDiscountOverride?: number;
    flatDiscountOverride?: MoneyProto;
  }): Promise<{
    initialBillingCycle?: BillingCyclePreviewProto;
    recurringBillingCycle?: BillingCyclePreviewProto;
  }> {
    const address = applyAddress ? getValidAddressForTaxCalculation() : {};
    // Base discounts come from slice if asked to reapply
    const discounts = reapplyExistingPromo ? getDiscountsFromSlice() : {};
    // Explicit overrides take precedence to avoid stale reads
    if (percentageDiscountOverride) {
      discounts.percentageDiscount = percentageDiscountOverride;
    } else if (flatDiscountOverride) {
      discounts.flatDiscount = flatDiscountOverride;
    }
    return createOrderAndBillingCyclePreview(
      true,
      address.billingAddress,
      address.shippingAddress,
      discounts.percentageDiscount,
      discounts.flatDiscount
    );
  }

  async function getOrder(orderUuid: string) {
    const newOrder = await getOrderRequest({ orderUuid });

    const billingCyclesPreview = await previewBillingCycles({
      paymentSchedule: getPaymentSchedule(newOrder),
    });
    const { initialBillingCycle, recurringBillingCycle } =
      extractBillingCycles(billingCyclesPreview);

    const trialSaleItemId =
      initialBillingCycle?.invoice?.lineItems?.[0]?.saleItemId;

    const newRecommendedPlan = convertCyclesToPlan({
      initialCycle: initialBillingCycle,
      recurringCycle: recurringBillingCycle,
      pspPlanId: null,
      legacyNoomPlanId: null,
      trialSaleItemId,
    });

    dispatch(setRecommendedPlan(newRecommendedPlan));

    dispatch(
      setCheckoutSaleItem({
        saleItemId: recurringBillingCycle?.invoice?.lineItems?.[0]?.saleItemId,
        trialSaleItemId:
          initialBillingCycle?.invoice?.lineItems?.[0]?.saleItemId,
      })
    );

    dispatch(
      setCheckoutOrders({
        order: newOrder,
        initialBillingCycle,
        recurringBillingCycle,
      })
    );

    // For Apple Purchase Linkout, trial fee is always 0
    if (isApplePurchaseLinkoutEnabled()) {
      dispatch(setTrialFee(0));
    }
  }

  async function initializeCheckout() {
    const { saleItem } = getStore().getState().checkoutOrders;
    const task = trackTask("InitializeOrdersCheckout", {
      accessCode,
      saleItemId: saleItem?.saleItemId,
      trialSaleItemId: saleItem?.trialSaleItemId,
    });
    try {
      task.start();
      await createOrderAndBillingCyclePreview(false);
      task.success();
    } catch (ex) {
      task.failed(ex);
      dispatch(setHasInitializationFailed(true));
      captureException(ex);
      dispatch(
        updateCheckoutState({
          purchaseErrorCode: errorConstants.errorCreateOrder,
        })
      );
    }
  }

  async function calculateSalesTaxForOrder() {
    try {
      if (
        (requiresPhysicalAddress() && hasValidPhysicalAddress()) ||
        (!requiresPhysicalAddress() && hasValidBillingAddress())
      ) {
        await previewOrderWithContext({
          applyAddress: true,
          reapplyExistingPromo: true,
        });
      }
    } catch (ex) {
      trackBuyflowEvent("CheckoutOrdersOperationFailed", {
        operationName: "calculateSalesTax",
        error: ex?.message,
      });
      captureException(ex);
    }
  }

  async function applyPromoCodeToOrder(
    promoCode: string,
    useAddressIfAvailable = true
  ): Promise<{
    success: boolean;
    initialBillingCycle?: BillingCyclePreviewProto;
    recurringBillingCycle?: BillingCyclePreviewProto;
  }> {
    // Switch to billing promo validation service once it is ready
    // https://github.com/noom/noom-contracts/blob/master/noom_contracts/billing/discounts/promotions_service.proto#L120
    // TODO: MGEXP-3026
    // Get the sale item directly from state to avoid using stale state from the hook
    const { legacyNoomPlanId } = getStore().getState().checkoutOrders?.saleItem;
    dispatch(updatePromoOrderStatus("in_progress"));
    const result = await dispatch(
      applyPromoCode({ noom_plan_id: legacyNoomPlanId }, promoCode)
    );
    const {
      promoDiscountAmount: newPromoDiscountAmount,
      promoDiscountPercentage: newPromoDiscountPercentage,
      promoCodeApplied: newPromoCodeApplied,
    } = result?.newPromoCode || {};
    if (newPromoCodeApplied) {
      try {
        const { initialBillingCycle, recurringBillingCycle } =
          await previewOrderWithContext({
            applyAddress: useAddressIfAvailable,
            reapplyExistingPromo: false,
            percentageDiscountOverride:
              newPromoDiscountPercentage !== 0
                ? newPromoDiscountPercentage
                : undefined,
            flatDiscountOverride:
              newPromoDiscountAmount !== 0
                ? convertNumberToMoney(
                    newPromoDiscountAmount,
                    DEFAULT_ORDERS_CURRENCY
                  )
                : undefined,
          });
        const recurring = recurringBillingCycle?.invoice?.lineItems?.[0];
        const discountAmount = convertMoneyProtoToNumber(
          recurring?.discountInfo?.promoAmount
        );
        dispatch(updatePromoDiscountAmount(discountAmount));
        dispatch(updatePromoOrderStatus("applied"));
        return { success: true, initialBillingCycle, recurringBillingCycle };
      } catch (ex) {
        trackBuyflowEvent("CheckoutOrdersOperationFailed", {
          operationName: "applyPromoDiscount",
          error: ex?.message,
        });
        captureException(ex);
        dispatch(
          setPromoError({
            promoCodeError: i18n.t("checkout:genericPromoError"),
            promoCodeErrorType: "UnknownError",
          })
        );
      }
    }
    dispatch(updatePromoOrderStatus("failed"));
    return { success: false };
  }

  return {
    initializeCheckout: useCallbackRef(initializeCheckout),
    createOrderAndBillingCyclePreview: useCallbackRef(
      createOrderAndBillingCyclePreview
    ),
    calculateSalesTaxForOrder: useCallbackRef(calculateSalesTaxForOrder),
    applyPromoCodeToOrder: useCallbackRef(applyPromoCodeToOrder),
    getOrder: useCallbackRef(getOrder),
  };
}
